import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Input, Card, Row, Col, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Employee } from '@/types/resources/employee';
import { User } from '@/types/users';
import dayjs from 'dayjs';
import UserFormSection from '@/components/common/UserFormSection';
import CurrencyInput from '@/components/common/CurrencyInput';
import ContactsFormSection from '@/components/common/ContactsFormSection';
import { useAuth } from '@/contexts/AuthContext';
import { addEmployee, updateEmployee } from '@/api/employees';
import { getUser } from '@/api/users';
import { removeUndefined } from '@/utils/removeUndefined';

const { Title } = Typography;

interface EmployeeModalProps {
  open: boolean;
  employee?: Employee | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EmployeeModal: React.FC<EmployeeModalProps> = ({
  open,
  employee,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['employees', 'common']);
  const [form] = Form.useForm();
  const { firestoreUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  
  // State to track found user from email lookup
  const [foundUser, setFoundUser] = useState<User | null>(null);

  // Fetch complete user data when editing
  useEffect(() => {
    const fetchUserData = async () => {
      if (employee?.user?.id && firestoreUser) {
        try {
          const user = await getUser(employee.user.id, firestoreUser);
          setUserData(user);
          // Update form with complete user data
          form.setFieldsValue({
            user: {
              id: user.id,
              displayName: user.displayName,
              email: user.email,
              phone: user.phoneNumber,
              username: user.username,
              language: user.language || 'en',
              currency: user.currency || 'USD'
            }
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          message.error(t('common:messages.fetchError'));
        }
      }
    };

    if (open && employee?.user?.id) {
      fetchUserData();
    }
  }, [open, employee?.user?.id, t, form, firestoreUser]);

  // Set initial values when modal opens or employee changes
  useEffect(() => {
    if (open && employee) {
      form.setFieldsValue({
        ...employee,
        joiningDate: employee.joiningDate ? dayjs(employee.joiningDate) : undefined,
        exitDate: employee.exitDate ? dayjs(employee.exitDate) : undefined,
        // Only set user data if we don't have complete user data yet
        ...(userData ? {} : {
          user: {
            id: employee.user?.id,
            displayName: employee.user?.displayName,
            email: employee.user?.email || '',
            phone: employee.user?.phoneNumber || '', // Map phoneNumber to phone for the form
            username: employee.user?.username || '',
            language: employee.user?.language || 'en',
            currency: employee.user?.currency || 'USD'
          }
        })
      });
    } else if (open) {
      form.resetFields();
      setFoundUser(null);
    }
  }, [open, employee, form, userData]);

  // Handle user found from email lookup
  const handleUserFound = (user: User | null) => {
    setFoundUser(user);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }

      const employeeData = removeUndefined({
        ...values,
        joiningDate: values.joiningDate ? values.joiningDate.toISOString() : undefined,
        exitDate: values.exitDate ? values.exitDate.toISOString() : undefined,
        // Include found user information if available
        existingUser: foundUser,
      });

      if (employee) {
        // Update existing employee
        await updateEmployee(employee.id, employeeData, firestoreUser);
        message.success(t('employees:messages.updateSuccess'));
      } else {
        // Create new employee
        await addEmployee(employeeData, firestoreUser);
        message.success(t('employees:messages.addSuccess'));
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error submitting employee:', error);
      if (error.message === 'auth/email-already-in-use') {
        message.error(t('common:messages.emailInUse'));
      } else if (error.message === 'auth/invalid-email') {
        message.error(t('common:messages.invalidEmail'));
      } else {
        message.error(t('common:messages.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={employee ? t('employees:actions.edit') : t('employees:actions.add')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnHidden
      width={800}
      confirmLoading={isSubmitting}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      okButtonProps={{ loading: isSubmitting }}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <UserFormSection 
          form={form} 
          initialValues={employee ? {
            user: {
              id: employee.user?.id,
              displayName: employee.user?.displayName,
              email: employee.user?.email || '',
              phoneNumber: employee.user?.phoneNumber || '',
              username: employee.user?.username || '',
              language: employee.user?.language || 'en',
              currency: employee.user?.currency || 'USD'
            }
          } : undefined}
          onUserFound={handleUserFound}
        />

        <Card style={{ marginTop: 16 }}>
          <Title level={5}>{t('employees:details.title')}</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="employeeId"
                label={t('employees:fields.employeeId')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Input 
                  placeholder={t('employees:fields.employeeIdPlaceholder')} 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="department"
                label={t('employees:fields.department')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select placeholder={t('employees:fields.departmentPlaceholder')}>
                  {Object.entries(t('employees:departments', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                    <Select.Option key={key} value={key}>{value}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="role"
                label={t('employees:fields.role')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select placeholder={t('employees:fields.rolePlaceholder')}>
                  {Object.entries(t('employees:roles', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                    <Select.Option key={key} value={key}>{value}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="status"
                label={t('employees:fields.status')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select placeholder={t('employees:fields.statusPlaceholder')}>
                  {Object.entries(t('employees:status', { returnObjects: true }) as [string, string][]).map(([key, value]) => (
                    <Select.Option key={key} value={key}>{value}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="salary"
                label={t('employees:fields.salary')}
              >
                <CurrencyInput />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="joiningDate"
                label={t('employees:fields.joiningDate')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <DatePicker 
                  format={t('employees:dateFormat')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="exitDate"
                label={t('employees:fields.exitDate')}
              >
                <DatePicker 
                  format={t('employees:dateFormat')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label={t('employees:fields.notes')}
          >
            <Input.TextArea placeholder={t('employees:fields.notesPlaceholder')} />
          </Form.Item>

          <Form.Item label={t('employees:fields.contacts')}>
            <ContactsFormSection t={t} />
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
}; 